import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, ValidationErrors } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { QuillModules } from 'ngx-quill';
import {
  MunilyTextAreaFieldErrorMessages,
  MunilyTextAreaFieldWidthLayout,
  MunilyTextAreaValueFormat,
} from './text-area-field.types';

/** Toolbar reducida: negrita, cursiva, listas numeradas/viñetas, enlace (equivalente al set previo en Froala). */
const DEFAULT_QUILL_TOOLBAR: QuillModules['toolbar'] = [
  ['bold', 'italic'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['link'],
];

@Component({
  selector: 'app-text-area-field',
  templateUrl: './text-area-field.component.html',
  styleUrls: ['./text-area-field.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextAreaFieldComponent implements OnInit, OnChanges, OnDestroy {
  private static _idSeq = 0;

  @Input() control?: FormControl;

  @Input() widthLayout: MunilyTextAreaFieldWidthLayout = 'ds';
  @Input() maxWidth?: string;
  @Input() label = '';
  @Input() placeholder = '';
  @Input() hint = '';
  @Input() disabled = false;
  @Input() successful = false;

  /** Altura mínima del área editable (px), alineada a usos tipo “descripción” en reservas. */
  @Input() minHeightPx = 120;

  /**
   * `html`: el FormControl almacena el HTML del editor (Quill).
   * `plain`: almacena texto plano (saltos de línea preservados); el editor sigue siendo WYSIWYG por dentro.
   */
  @Input() valueFormat: MunilyTextAreaValueFormat = 'html';

  /**
   * `true`: editor enriquecido (toolbar de negrita/cursiva/listas/enlace).
   * `false`: solo texto normal — sin toolbar y sin formatos permitidos.
   */
  @Input() richText = true;

  /** Contador de caracteres (estilo DS / Figma). Por defecto activo. */
  @Input() hasCharacterCounter = true;

  /**
   * Tope mostrado en el contador (`actual / max`). Por defecto 500; usa `null` para solo mostrar el total sin máximo.
   */
  @Input() maxCharacters: number | null = 500;

  /** Reduce re-render / eventos por tecla (ms). */
  @Input() editorDebounceMs = 150;

  /**
   * Fusión superficial con `QuillModules` (p. ej. sustituir `toolbar` completo o añadir `clipboard`).
   */
  @Input() quillModulesExtra: Partial<QuillModules> = {};

  @Input() errorMessage = '';
  @Input() errorMessages: MunilyTextAreaFieldErrorMessages = {};

  @Output() valueChange = new EventEmitter<string>();

  public hostClasses: string[] = [];

  readonly labelId = `munily-ta-label-${TextAreaFieldComponent._idSeq++}`;
  readonly fallbackControl = new FormControl('');

  /** HTML interno del editor cuando `valueFormat === 'plain'`. */
  readonly editorHtmlControl = new FormControl('');

  /** Limita formatos pegados / API al mismo subconjunto que la toolbar (vacío en modo solo texto). */
  get quillFormats(): string[] {
    return this.richText ? ['bold', 'italic', 'list', 'link'] : [];
  }

  quillModules: QuillModules = { toolbar: DEFAULT_QUILL_TOOLBAR };

  /** Valor calculado (debounced) para evitar parsear HTML en cada CD. */
  displayedCharacterCountValue = 0;

  private readonly destroy$ = new Subject<void>();

  /** Evita bucle HTML → plain → HTML cuando el texto plano ya coincide. */
  private syncingPlainFromEditor = false;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly host: ElementRef<HTMLElement>,
  ) {}

  @HostBinding('class.munily-ta-host--ds-default')
  get hostDsDefaultMaxWidth(): boolean {
    return this.widthLayout === 'ds' && !this.maxWidth;
  }

  @HostBinding('style.max-width')
  get hostMaxWidthStyle(): string | null {
    if (this.widthLayout === 'fill') {
      return 'none';
    }
    if (this.widthLayout === 'ds' && this.maxWidth) {
      return this.maxWidth;
    }
    return null;
  }

  get resolvedControl(): FormControl {
    return this.control ?? this.fallbackControl;
  }

  /** Control enlazado a Quill (siempre HTML en el modelo del editor). */
  get formControlForEditor(): FormControl {
    return this.valueFormat === 'plain' ? this.editorHtmlControl : this.resolvedControl;
  }

  get quillEditorStyles(): Record<string, string> {
    return { minHeight: `${this.minHeightPx}px` };
  }

  /** Caracteres “visibles” para el contador: texto plano (en `html` se deriva del HTML). */
  get displayedCharacterCount(): number {
    return this.displayedCharacterCountValue;
  }

  get resolvedErrorMessage(): string {
    if (this.errorMessage) return this.errorMessage;
    const errors = this.resolvedControl.errors;
    if (!errors) return '';
    return this.firstErrorMessage(errors);
  }

  ngOnInit(): void {
    this.seedPlainEditorFromControl();
    this.rebuildQuillModules();
    this.syncDisabled();
    this.wireControlUpdates();
    this.syncHostClasses();
    this.recomputeCharacterCount();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['quillModulesExtra'] || changes['richText']) {
      this.rebuildQuillModules();
    }
    if (changes['disabled']) {
      this.syncDisabled();
    }
    if (changes['control'] && !changes['control'].firstChange) {
      this.destroy$.next();
      this.seedPlainEditorFromControl();
      this.syncDisabled();
      this.wireControlUpdates();
    }
    if (changes['valueFormat'] && !changes['valueFormat'].firstChange) {
      this.destroy$.next();
      this.seedPlainEditorFromControl();
      this.syncDisabled();
      this.wireControlUpdates();
      this.recomputeCharacterCount();
    }

    if (changes['disabled'] || changes['successful'] || changes['control']) {
      this.syncHostClasses();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  showError(): boolean {
    const c = this.resolvedControl;
    return c.invalid && (c.touched || c.dirty);
  }

  showSuccessOutline(): boolean {
    return this.successful && !this.showError();
  }

  onEditorBlur(): void {
    this.flushPendingValue();
    this.resolvedControl.markAsTouched();
    this.cdr.markForCheck();
    this.syncHostClasses();
  }

  /**
   * Forces Quill → FormControl sync immediately.
   * Needed when the parent submits before ngx-quill's debounce flushes
   * (e.g. type comment → click create without blurring the editor).
   */
  flushPendingValue(): string {
    if (this.valueFormat === 'plain') {
      const editor = this.host.nativeElement.querySelector(
        '.ql-editor',
      ) as HTMLElement | null;
      const liveHtml = editor?.innerHTML;
      if (liveHtml != null) {
        const plain = this.htmlToPlain(liveHtml);
        const cur = this.resolvedControl.value ?? '';
        if (plain !== cur) {
          this.syncingPlainFromEditor = true;
          this.resolvedControl.setValue(plain, { emitEvent: true });
          this.syncingPlainFromEditor = false;
        }
        this.editorHtmlControl.setValue(liveHtml, { emitEvent: false });
        this.recomputeCharacterCount(plain);
        return plain;
      }
      return String(this.resolvedControl.value ?? '');
    }

    const editor = this.host.nativeElement.querySelector(
      '.ql-editor',
    ) as HTMLElement | null;
    const liveHtml = editor?.innerHTML;
    if (liveHtml != null) {
      const cur = this.resolvedControl.value ?? '';
      if (liveHtml !== cur) {
        this.resolvedControl.setValue(liveHtml, { emitEvent: true });
      }
      this.recomputeCharacterCount(liveHtml);
      return liveHtml;
    }
    return String(this.resolvedControl.value ?? '');
  }

  /**
   * H-15 / Tickets: Quill only focuses when the first content line is hit.
   * Clicks on the empty padded area of the shell focus `.ql-editor` instead.
   */
  onShellClick(event: MouseEvent): void {
    if (this.disabled) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (target?.closest('.ql-toolbar')) {
      return;
    }
    const shell = event.currentTarget as HTMLElement;
    const editor = shell.querySelector('.ql-editor') as HTMLElement | null;
    editor?.focus();
  }

  private syncHostClasses(): void {
    const next: string[] = [];
    if (this.showError()) next.push('munily-text-area-field--error');
    if (this.disabled) next.push('munily-text-area-field--disabled');
    if (this.showSuccessOutline()) next.push('munily-text-area-field--success');
    this.hostClasses = next;
  }

  private seedPlainEditorFromControl(): void {
    if (this.valueFormat !== 'plain') {
      return;
    }
    this.editorHtmlControl.setValue(this.plainToHtml(this.resolvedControl.value ?? ''), { emitEvent: false });
  }

  private rebuildQuillModules(): void {
    const base: QuillModules = {
      toolbar: this.richText ? DEFAULT_QUILL_TOOLBAR : false,
    };
    const extra = this.quillModulesExtra ?? {};
    this.quillModules = { ...base, ...extra };
    this.cdr.markForCheck();
  }

  private syncDisabled(): void {
    const ctrl = this.resolvedControl;
    if (this.disabled && ctrl.enabled) {
      ctrl.disable({ emitEvent: false });
    } else if (!this.disabled && ctrl.disabled) {
      ctrl.enable({ emitEvent: false });
    }
    if (this.valueFormat === 'plain') {
      if (this.disabled && this.editorHtmlControl.enabled) {
        this.editorHtmlControl.disable({ emitEvent: false });
      } else if (!this.disabled && this.editorHtmlControl.disabled) {
        this.editorHtmlControl.enable({ emitEvent: false });
      }
    }
  }

  private wireControlUpdates(): void {
    const ctrl = this.resolvedControl;
    ctrl.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.syncHostClasses();
      this.cdr.markForCheck();
    });
    ctrl.valueChanges
      .pipe(takeUntil(this.destroy$), debounceTime(this.editorDebounceMs), distinctUntilChanged())
      .subscribe((v) => {
        this.valueChange.emit(v ?? '');
        this.recomputeCharacterCount(String(v ?? ''));
        this.syncHostClasses();
        this.cdr.markForCheck();
      });

    if (this.valueFormat === 'plain') {
      this.wirePlainValueBridge();
    }
  }

  /** Sincroniza HTML (Quill) ↔ texto plano (FormControl padre). */
  private wirePlainValueBridge(): void {
    this.editorHtmlControl.valueChanges
      .pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe((html) => {
        const plain = this.htmlToPlain(html ?? '');
        const cur = this.resolvedControl.value ?? '';
        if (plain === cur) {
          return;
        }
        this.syncingPlainFromEditor = true;
        this.resolvedControl.setValue(plain, { emitEvent: true });
        this.syncingPlainFromEditor = false;
      });

    this.resolvedControl.valueChanges
      .pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe((plain) => {
        if (this.valueFormat !== 'plain' || this.syncingPlainFromEditor) {
          return;
        }
        const html = this.plainToHtml(plain ?? '');
        const cur = this.editorHtmlControl.value ?? '';
        if (html !== cur) {
          this.editorHtmlControl.setValue(html, { emitEvent: false });
        }
      });
  }

  private htmlToPlain(html: string): string {
    if (!html || !String(html).trim()) {
      return '';
    }
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent ?? '';
  }

  private recomputeCharacterCount(value?: string): void {
    const v = value ?? String(this.resolvedControl.value ?? '');
    if (!v) {
      this.displayedCharacterCountValue = 0;
      return;
    }
    this.displayedCharacterCountValue = this.valueFormat === 'plain' ? v.length : this.htmlToPlain(v).length;
  }

  private plainToHtml(plain: string | null): string {
    if (plain == null || plain === '') {
      return '<p><br></p>';
    }
    const lines = String(plain).split(/\n/);
    return lines.map((line) => `<p>${this.escapeHtml(line)}</p>`).join('');
  }

  private escapeHtml(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  private firstErrorMessage(errors: ValidationErrors): string {
    const key = Object.keys(errors)[0];
    const custom = this.errorMessages[key];
    if (custom) return custom;

    const err = errors[key];
    if (key === 'required') return 'Este campo es obligatorio';
    if (key === 'minlength' && err?.requiredLength != null) return `Mínimo ${err.requiredLength} caracteres`;
    if (key === 'maxlength' && err?.requiredLength != null) return `Máximo ${err.requiredLength} caracteres`;
    if (typeof err === 'object' && err?.message) return String(err.message);
    return 'Valor no válido';
  }
}
